import React, { useEffect, useState, useCallback } from 'react';
import './App.css';
import { Toaster } from 'sonner';
import { Sidebar } from './components/brahmastra/Sidebar';
import { TopHeader } from './components/brahmastra/TopHeader';
import { ChatWorkspace } from './components/brahmastra/ChatWorkspace';
import { QuizPage } from './components/brahmastra/QuizPage';
import { FlashcardsPage } from './components/brahmastra/FlashcardsPage';
import { DiaryDrawer } from './components/brahmastra/DiaryDrawer';
import { PremiumModal } from './components/brahmastra/PremiumModal';
import { SettingsModal } from './components/brahmastra/SettingsModal';
import { Sheet, SheetContent } from './components/ui/sheet';
import { storage } from './lib/storage';

const makeChat = () => ({
  id: `c_${Date.now()}`,
  title: 'New Chat',
  messages: [],
  conversationId: '',
  createdAt: Date.now(),
});

function App() {
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [guruId, setGuruId] = useState('hindi-sahitya');
  const [theme, setTheme] = useState('dark');
  const [view, setView] = useState('chat'); // 'chat' | 'quiz' | 'flashcards'
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [diaryOpen, setDiaryOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [gami, setGami] = useState({ xp: 0 });

  // Hydrate from localStorage
  useEffect(() => {
    const storedChats = storage.get(storage.KEYS.CHATS, []);
    const storedActive = storage.get(storage.KEYS.ACTIVE_CHAT, null);
    setChats(storedChats);
    setActiveChatId(storedActive && storedChats.find(c => c.id === storedActive) ? storedActive : null);
    setGuruId(storage.get(storage.KEYS.GURU, 'hindi-sahitya'));
    setTheme(storage.get(storage.KEYS.THEME, 'dark'));
    setGami(storage.get(storage.KEYS.GAMI, { xp: 0 }));
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'dark') html.classList.add('dark');
    else html.classList.remove('dark');
    storage.set(storage.KEYS.THEME, theme);
  }, [theme]);

  useEffect(() => { storage.set(storage.KEYS.CHATS, chats); }, [chats]);
  useEffect(() => { storage.set(storage.KEYS.ACTIVE_CHAT, activeChatId); }, [activeChatId]);
  useEffect(() => { storage.set(storage.KEYS.GURU, guruId); }, [guruId]);

  useEffect(() => {
    const handler = () => setGami(storage.get(storage.KEYS.GAMI, { xp: 0 }));
    window.addEventListener('brahmastra:xp', handler);
    return () => window.removeEventListener('brahmastra:xp', handler);
  }, []);

  const handleNewChat = useCallback(() => {
    const c = makeChat();
    setChats(prev => [c, ...prev]);
    setActiveChatId(c.id);
    setView('chat');
    setDesktopSidebarOpen(true); // re-open on new chat (sidebar visible)
    setMobileSidebarOpen(false);
  }, []);

  const handleUpdateChat = useCallback((updated) => {
    setChats(prev => {
      const idx = prev.findIndex(c => c.id === updated.id);
      if (idx === -1) return [updated, ...prev];
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...updated };
      if (idx > 0) {
        const [m] = copy.splice(idx, 1);
        copy.unshift(m);
      }
      return copy;
    });
  }, []);

  const activeChat = chats.find(c => c.id === activeChatId) || null;

  useEffect(() => {
    if (!activeChat && chats.length === 0) {
      const c = makeChat();
      setChats([c]);
      setActiveChatId(c.id);
    } else if (!activeChat && chats.length > 0) {
      setActiveChatId(chats[0].id);
    }
  }, [activeChat, chats]);

  const openQuiz = () => { setView('quiz'); setMobileSidebarOpen(false); };
  const openFlashcards = () => { setView('flashcards'); setMobileSidebarOpen(false); };
  const openPremium = () => { setPremiumOpen(true); setMobileSidebarOpen(false); };
  const openSettings = () => { setSettingsOpen(true); setMobileSidebarOpen(false); };
  const openDiary = () => { setDiaryOpen(true); setMobileSidebarOpen(false); };
  const backToChat = () => setView('chat');

  const sidebarProps = (closeMobile = false) => ({
    chats,
    activeChatId,
    onSelectChat: (id) => { setActiveChatId(id); setView('chat'); if (closeMobile) setMobileSidebarOpen(false); },
    onNewChat: handleNewChat,
    guruId,
    onGuruChange: setGuruId,
    onOpenDiary: openDiary,
    onOpenSettings: openSettings,
    onOpenPremium: openPremium,
    gami,
    ...(closeMobile ? { onCloseMobile: () => setMobileSidebarOpen(false) } : {}),
  });

  return (
    <div className="App flex min-h-[100dvh] h-[100dvh] w-screen bg-app text-foreground overflow-hidden" style={{ minHeight: '100dvh', height: '100dvh' }} data-testid="app-root">
      {/* Desktop Sidebar - collapsible */}
      <div
        className={`hidden md:block h-full transition-all duration-300 ease-in-out overflow-hidden ${
          desktopSidebarOpen ? 'w-[280px]' : 'w-0'
        }`}
        data-testid="desktop-sidebar-container"
      >
        {desktopSidebarOpen && <Sidebar {...sidebarProps(false)} />}
      </div>

      {/* Mobile Sidebar (Sheet) */}
      <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
        <SheetContent
          side="left"
          className="w-[280px] sm:max-w-[280px] p-0 bg-card border-r border-border"
          data-testid="mobile-sidebar"
        >
          <Sidebar {...sidebarProps(true)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 min-h-0 flex flex-col h-full">
        <TopHeader
          onOpenQuiz={openQuiz}
          onOpenFlashcards={openFlashcards}
          onOpenPremium={openPremium}
          onToggleSidebar={() => {
            // On mobile open the sheet; on desktop toggle the inline sidebar
            if (window.matchMedia('(min-width: 768px)').matches) {
              setDesktopSidebarOpen(o => !o);
            } else {
              setMobileSidebarOpen(true);
            }
          }}
          onLogoClick={handleNewChat}
          theme={theme}
          onToggleTheme={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
        />

        {view === 'chat' && activeChat && (
          <ChatWorkspace
            chat={activeChat}
            onUpdateChat={handleUpdateChat}
            guruId={guruId}
            onFirstMessage={() => setDesktopSidebarOpen(false)}
          />
        )}
        {view === 'quiz' && <QuizPage onBack={backToChat} />}
        {view === 'flashcards' && <FlashcardsPage onBack={backToChat} />}
      </div>

      <DiaryDrawer open={diaryOpen} onOpenChange={setDiaryOpen} />
      <PremiumModal open={premiumOpen} onOpenChange={setPremiumOpen} />
      <SettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        theme={theme}
        onToggleTheme={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
        onOpenPremium={openPremium}
      />

      <Toaster theme="dark" position="bottom-right" richColors closeButton />
    </div>
  );
}

export default App;
